import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ChevronRight, Pencil, Plus, Search, X } from 'lucide-react';
import Button from '../ui/Button';
import sprayService from '../../services/spray';
import type { ApiError } from '../../types';
import type { SpraySituationDetail } from '../../types/spray';
import { buildSituationTree, eligibleTargets, type SituationGroupNode } from '@/utils/situationGroups';
import { useAuth } from '../../context/AuthContext';
import { isPlatformStaff } from '@/utils/auth';

/**
 * Spray situations, laid out as the groups the chemical list is drawn from.
 *
 * Two relations, and the page says which is which because they are not
 * interchangeable: a spelling covers its group both ways, a broader group
 * covers what sits under it ONE way. Getting that backwards offers an
 * almond-only product on a walnut block.
 *
 * Shared and own (2026-10-16): the shared situations (APVMA hosts, curated
 * groups) are kept by FactreeTech and read-only here; a situation the
 * organisation adds is its own. The server lists only the situations the
 * organisation uses: those its chemicals name, its own, and those its crops
 * are sprayed as.
 */

type Relation = 'standalone' | 'same_as' | 'parent';

interface EditState {
  row: SpraySituationDetail | null; // null = new
  name: string;
  description: string;
  relation: Relation;
  targetId: number | null;
  caveat: string;
}

const inputClass =
  'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

function matches(r: SpraySituationDetail, q: string) {
  return r.name.toLowerCase().includes(q) || (r.crop_types ?? []).some(c => c.toLowerCase().includes(q));
}

function nodeMatches(n: SituationGroupNode, q: string): boolean {
  return matches(n.row, q) || n.synonyms.some(s => matches(s, q)) || n.children.some(c => nodeMatches(c, q));
}

export default function SituationGroupsTab() {
  const { user } = useAuth();
  const staff = isPlatformStaff(user);
  // A server from before shared/own sends no is_shared: everything stays editable, as it was.
  const canChange = (r: SpraySituationDetail) => r.is_shared !== true || staff;
  const [notice, setNotice] = useState<string | null>(null);
  const [rows, setRows] = useState<SpraySituationDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    sprayService.getSituationsDetail()
      .then(res => {
        if (res.success && res.data) { setRows(res.data); setLoadError(null); }
        else setLoadError('Could not load situations');
      })
      .catch(() => setLoadError('Could not load situations'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const byId = useMemo(() => new Map(rows.map(r => [r.id, r])), [rows]);
  const { groups, ungrouped } = useMemo(() => buildSituationTree(rows), [rows]);
  const q = query.trim().toLowerCase();
  const shownGroups = q ? groups.filter(g => nodeMatches(g, q)) : groups;
  const shownUngrouped = q ? ungrouped.filter(r => matches(r, q)) : ungrouped;
  const targets = useMemo(
    () => (edit?.row ? eligibleTargets(rows, edit.row.id) : []),
    [rows, edit?.row],
  );

  const openEdit = (r: SpraySituationDetail) => {
    setError(null);
    setEdit({
      row: r,
      name: r.name,
      description: r.description ?? '',
      relation: r.same_as_id != null ? 'same_as' : r.parent_id != null ? 'parent' : 'standalone',
      targetId: r.same_as_id ?? r.parent_id ?? null,
      caveat: r.caveat ?? '',
    });
  };
  const openAdd = () => {
    setError(null);
    setEdit({ row: null, name: '', description: '', relation: 'standalone', targetId: null, caveat: '' });
  };

  const save = async () => {
    if (!edit) return;
    if (!edit.name.trim()) { setError('Name is required'); return; }
    if (edit.relation !== 'standalone' && !edit.targetId) {
      setError(edit.relation === 'same_as' ? 'Choose what it is a spelling of' : 'Choose the broader group');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let id = edit.row?.id;
      if (!edit.row) {
        const res = await sprayService.createSituation(edit.name.trim(), edit.description || undefined);
        if (res.data?.existing) {
          // A shared situation already has this name: no copy is made.
          setNotice(`"${res.data.name}" is a shared situation. It shows here once one of your chemicals is registered for it.`);
          setEdit(null);
          load();
          return;
        }
        id = res.data?.id;
      } else if (edit.name.trim() !== edit.row.name || (edit.description || null) !== (edit.row.description || null)) {
        await sprayService.updateSituation(edit.row.id, { name: edit.name.trim(), description: edit.description || undefined });
      }
      if (id != null) {
        const before = edit.row;
        const sameAs = edit.relation === 'same_as' ? edit.targetId : null;
        const parent = edit.relation === 'parent' ? edit.targetId : null;
        const caveat = edit.caveat.trim() || null;
        if (!before || before.same_as_id !== sameAs || before.parent_id !== parent || (before.caveat || null) !== caveat) {
          await sprayService.setSituationGroup(id, { same_as_id: sameAs, parent_id: parent, caveat });
        }
      }
      setEdit(null);
      load();
    } catch (err) {
      setError((err as ApiError)?.message || 'Could not save situation');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (!edit?.row) return;
    await sprayService.updateSituation(edit.row.id, { is_active: !edit.row.is_active });
    setEdit(null);
    load();
  };

  const remove = async () => {
    if (!edit?.row) return;
    if (!confirm(`Delete "${edit.row.name}"? If it has registrations it will be deactivated instead.`)) return;
    await sprayService.deleteSituation(edit.row.id);
    setEdit(null);
    load();
  };

  const usage = (r: SpraySituationDetail) => {
    const parts: string[] = [];
    if (r.registration_count) parts.push(`${r.registration_count} reg${r.registration_count === 1 ? '' : 's'}`);
    if (r.crop_types?.length) parts.push(`crop: ${r.crop_types.join(', ')}`);
    return parts.join(' · ');
  };

  const renderNode = (n: SituationGroupNode, depth: number) => (
    <li key={n.row.id}>
      <div
        className="flex items-start gap-2 py-2 border-b border-neutral-100"
        style={{ paddingLeft: `${Math.min(depth, 4) * 16}px` }}
      >
        {depth > 0 && <ChevronRight className="h-4 w-4 text-neutral-300 flex-shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-medium break-words ${n.row.is_active ? 'text-neutral-800' : 'text-neutral-400 line-through'}`}>
              {n.row.name}
            </span>
            <span className="text-xs text-neutral-400">{n.registrations} regs</span>
            {n.row.is_shared && <SharedBadge />}
            {n.row.caveat && <AlertCircle className="h-3.5 w-3.5 text-amber-600" aria-label="Has a caveat" />}
          </div>
          {n.row.crop_types && n.row.crop_types.length > 0 && (
            <p className="text-xs text-neutral-500 break-words">Crop types: {n.row.crop_types.join(', ')}</p>
          )}
          {n.synonyms.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              <span className="text-xs text-neutral-400">Also spelled:</span>
              {n.synonyms.map(s => (
                <button
                  key={s.id}
                  type="button"
                  disabled={!canChange(s)}
                  onClick={() => openEdit(s)}
                  title={usage(s) || undefined}
                  className={`text-xs px-1.5 py-0.5 rounded border break-words text-left ${
                    s.is_active ? 'border-neutral-200 text-neutral-600 hover:border-neutral-400' : 'border-neutral-100 text-neutral-400 line-through'
                  }`}
                >
                  {s.name}
                  {s.caveat && ' ⚠'}
                </button>
              ))}
            </div>
          )}
        </div>
        {canChange(n.row) && (
          <button
            type="button"
            onClick={() => openEdit(n.row)}
            className="p-1 text-neutral-400 hover:text-neutral-700 rounded flex-shrink-0"
            aria-label={`Edit ${n.row.name}`}
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>
      {n.children.length > 0 && <ul>{n.children.map(c => renderNode(c, depth + 1))}</ul>}
    </li>
  );

  if (loading && !rows.length) return <p className="text-sm text-neutral-500">Loading…</p>;
  if (loadError && !rows.length) return <p className="text-sm text-red-600">{loadError}</p>;

  const target = edit?.targetId != null ? byId.get(edit.targetId) : undefined;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h3 className="font-semibold text-neutral-800">Spray Situations</h3>
          <p className="text-sm text-neutral-500 mt-0.5">
            Labels name one crop many ways. Group them so a spray finds every registration that covers the crop.
          </p>
          <p className="text-xs text-neutral-500 mt-1">
            Only the situations you use are listed. Shared ones are kept by FactreeTech: ask support to change one.
            A situation you add is your own.
          </p>
        </div>
        <Button size="sm" onClick={openAdd} className="flex-shrink-0">
          <Plus className="h-4 w-4 mr-1" /> Add situation
        </Button>
      </div>

      <div className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 mb-4 space-y-1">
        <p><strong>Also spelled</strong> — the same thing named differently (TREE NUT, Nuts). Covers both ways.</p>
        <p>
          <strong>Under a broader group</strong> — the broader group's registrations cover it, never the reverse.
          ALMOND sits under tree nuts, so almonds get tree-nut products; walnuts do not get almond-only ones.
        </p>
      </div>

      {notice && (
        <p className="text-xs text-neutral-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4 break-words">{notice}</p>
      )}

      <div className="flex items-center gap-2 border border-neutral-300 rounded-lg px-3 py-2 mb-4">
        <Search className="h-4 w-4 text-neutral-400 flex-shrink-0" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Find a situation or crop type…"
          className="flex-1 min-w-0 text-sm outline-none bg-transparent"
        />
      </div>

      {shownGroups.length > 0 && (
        <section className="mb-6">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Groups</h4>
          <ul>{shownGroups.map(g => renderNode(g, 0))}</ul>
        </section>
      )}

      {shownUngrouped.length > 0 && (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-1">Not grouped</h4>
          <p className="text-xs text-neutral-400 mb-1">
            These match only themselves. Leave ornamentals, nursery stock and non-crop uses here.
          </p>
          <ul>
            {shownUngrouped.map(r => (
              <li key={r.id} className="flex items-start gap-2 py-2 border-b border-neutral-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <span className={`font-medium break-words ${r.is_active ? 'text-neutral-800' : 'text-neutral-400 line-through'}`}>
                    {r.name}
                  </span>
                  {r.is_shared && <> {' '}<SharedBadge /></>}
                  {usage(r) && <p className="text-xs text-neutral-500 break-words">{usage(r)}</p>}
                </div>
                {canChange(r) && (
                  <button
                    type="button"
                    onClick={() => openEdit(r)}
                    className="p-1 text-neutral-400 hover:text-neutral-700 rounded flex-shrink-0"
                    aria-label={`Edit ${r.name}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {!shownGroups.length && !shownUngrouped.length && (
        <p className="text-sm text-neutral-400 text-center py-8">{q ? 'Nothing matches.' : 'No situations yet. Add one above.'}</p>
      )}

      {edit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h3 className="font-semibold text-neutral-800 break-words min-w-0">
                {edit.row ? 'Edit situation' : 'New situation'}
              </h3>
              <button onClick={() => setEdit(null)} className="text-neutral-400 hover:text-neutral-600 flex-shrink-0" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label htmlFor="sit-name" className="block text-xs font-medium text-neutral-600 mb-1">Name *</label>
                <input
                  id="sit-name"
                  value={edit.name}
                  onChange={e => setEdit({ ...edit, name: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. Stone fruit"
                />
                {edit.row?.source === 'apvma' && (
                  <p className="text-xs text-neutral-400 mt-1">
                    APVMA host {edit.row.apvma_hostcode}. A shared situation: renaming it changes it for every organisation.
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="sit-desc" className="block text-xs font-medium text-neutral-600 mb-1">Description</label>
                <input
                  id="sit-desc"
                  value={edit.description}
                  onChange={e => setEdit({ ...edit, description: e.target.value })}
                  className={inputClass}
                  placeholder="Optional"
                />
              </div>

              {edit.row && (
                <fieldset className="space-y-2">
                  <legend className="block text-xs font-medium text-neutral-600 mb-1">Grouping</legend>
                  {([
                    ['standalone', 'Not grouped', 'Matches only itself.'],
                    ['same_as', 'Another spelling of…', 'Same crop, named differently. Covers both ways.'],
                    ['parent', 'Covered by a broader group…', "The broader group's registrations apply here. Not the reverse."],
                  ] as [Relation, string, string][]).map(([value, label, hint]) => (
                    <label key={value} className="flex items-start gap-2 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="sit-relation"
                        className="mt-1"
                        checked={edit.relation === value}
                        onChange={() => setEdit({ ...edit, relation: value, targetId: value === edit.relation ? edit.targetId : null })}
                      />
                      <span className="min-w-0">
                        <span className="text-neutral-800">{label}</span>
                        <span className="block text-xs text-neutral-500">{hint}</span>
                      </span>
                    </label>
                  ))}
                  {edit.relation !== 'standalone' && (
                    <select
                      aria-label={edit.relation === 'same_as' ? 'Spelling of' : 'Broader group'}
                      value={edit.targetId ?? ''}
                      onChange={e => setEdit({ ...edit, targetId: e.target.value ? Number(e.target.value) : null })}
                      className={inputClass}
                    >
                      <option value="">Choose…</option>
                      {targets.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.same_as_id != null ? ` (spelling of ${byId.get(t.same_as_id)?.name ?? '…'})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {edit.relation === 'same_as' && target?.same_as_id != null && (
                    <p className="text-xs text-neutral-500">
                      Joins the {byId.get(target.same_as_id)?.name} group.
                    </p>
                  )}
                  {edit.relation === 'same_as' && rows.some(r => r.same_as_id === edit.row?.id) && (
                    <p className="text-xs text-neutral-500">Its own spellings move with it.</p>
                  )}
                </fieldset>
              )}

              {edit.row && (
                <div>
                  <label htmlFor="sit-caveat" className="block text-xs font-medium text-neutral-600 mb-1">Caveat</label>
                  <input
                    id="sit-caveat"
                    value={edit.caveat}
                    onChange={e => setEdit({ ...edit, caveat: e.target.value })}
                    className={inputClass}
                    placeholder="e.g. Label lists exceptions — check before use"
                  />
                  <p className="text-xs text-neutral-400 mt-1">Shown beside every registration matched through this situation.</p>
                </div>
              )}

              {edit.row && usage(edit.row) && (
                <p className="text-xs text-neutral-500 break-words">{usage(edit.row)}</p>
              )}
              {error && <p className="text-xs text-red-600 break-words">{error}</p>}
            </div>

            <div className="flex flex-wrap gap-2 mt-5">
              {edit.row && (
                <>
                  <Button variant="secondary" size="sm" onClick={toggleActive} disabled={saving}>
                    {edit.row.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={remove} disabled={saving}>Delete</Button>
                </>
              )}
              <div className="flex gap-2 ml-auto">
                <Button variant="secondary" size="sm" onClick={() => setEdit(null)}>Cancel</Button>
                <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SharedBadge() {
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-neutral-100 text-neutral-600 border border-neutral-200"
      title="Shared by every organisation and kept by FactreeTech"
    >
      Shared
    </span>
  );
}
